import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Progress, Row, Tooltip, message } from 'antd';
import { MentionsInput, Mention } from 'react-mentions';
import {
	AudioOutlined,
	CameraOutlined,
	DeleteOutlined,
	SendOutlined,
	VideoCameraAddOutlined,
} from '@ant-design/icons';
import { compareStrings, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { parseMessageToElements } from '../../utils/ChatUtils';
import { imageMimeTypes, imageUploadValidation } from '../../utils/ImageUtils';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { useNoteSender, useMentionSearch, useMediaUpload } from '../../hooks/useNoteSending';
import { ImageThumbnails } from '../image_thumbnails/ImageThumbnails';
import { UserName } from '../name_component/user/UserNameComponent';
import ChatUploader from '../ChatInput/ChatUploader';
import AudioRecorder from '../audio_recorder/AudioRecorder';
import moment from 'moment';
import './RowChatInput.less';

const videoMimeTypes = [
	'video/3gpp',
	'video/mp4',
	'video/mpeg',
	'video/ogg',
	'video/quicktime',
	'video/webm',
	'video/x-m4v',
	'video/ms-asf',
	'video/x-ms-wmv',
	'video/x-msvideo',
];

interface RowChatInputProps {
	workOrder: any;
	onNoteSent?: (data: any) => void;
	onCancel?: () => void;
	autoFocus?: boolean;
}

const RowChatInput: React.FC<RowChatInputProps> = ({
	workOrder,
	onNoteSent,
	onCancel,
	autoFocus = false,
}) => {
	const { sendNote, currentUser, workOrderId } = useNoteSender(workOrder);
	const { onSearch } = useMentionSearch(workOrder);

	const wrappedSendNote = useCallback(
		(entity: any) => {
			return sendNote(entity)
				.then((data: any) => {
					message.success('Note sent');
					if (onNoteSent) onNoteSent(data);
					return data;
				})
				.catch((err: any) => {
					message.error('Failed to send note');
					throw err;
				});
		},
		[sendNote, onNoteSent]
	);

	const media = useMediaUpload(workOrderId, wrappedSendNote, currentUser);

	const [noteText, setNoteText] = useState('');
	const containerRef = useRef<HTMLFormElement | null>(null);

	// Auto-focus the text input on mount
	useEffect(() => {
		if (autoFocus && containerRef.current) {
			const textarea = containerRef.current.querySelector('textarea');
			if (textarea) textarea.focus();
		}
	}, [autoFocus]);

	const handleSendAudio = (blob: Blob, file: File) => {
		media.handleSendAudio(blob, file);
	};

	const handleSubmit = (e: any) => {
		if (e) stopDefaultEvents(e);
		const trimmedText = noteText.trim();
		if (!trimmedText) return;

		const { elements, text } = parseMessageToElements(noteText);
		const entity = {
			text,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
			elements,
		};

		wrappedSendNote(entity)
			.then(() => {
				setNoteText('');
			})
			.catch(() => {
				// text preserved on failure — wrappedSendNote already shows error message
			});
	};

	const beforeVideoUpload = (file: any) => {
		const isVideo = videoMimeTypes.indexOf(file.type) !== -1;
		if (!isVideo) {
			message.error("This file either isn't a video or is not in a file type we support.");
		}
		return isVideo;
	};

	const hasText = noteText && noteText.trim().length > 0;
	const multiplePhotos = media.photoLinks.length > 1;

	return (
		<div className="w-full">
			{/* Photo preview modal */}
			<Modal
				visible={media.photoPreviewModalVisible}
				width={600}
				title={
					multiplePhotos
						? `Send these photos to WO #${workOrderId}?`
						: `Send this photo to WO #${workOrderId}?`
				}
				okText="Send"
				onCancel={media.onPhotoCancel}
				onOk={media.onPhotoSubmit}
				closable={false}
			>
				<Row className="gap-3">
					{media.photoLinks.map((photoLink, idx) => (
						<ImageThumbnails
							key={idx}
							imageWidth={100}
							imageHeight={100}
							photoStrings={[photoLink]}
						/>
					))}
				</Row>
			</Modal>

			{/* Video preview modal */}
			<Modal
				visible={media.videoPreviewModalVisible}
				width={600}
				title={`Send this video to WO #${workOrderId}?`}
				okText="Send"
				onCancel={media.onVideoCancel}
				onOk={media.onVideoSubmit}
				closable={false}
			>
				<video width="100%" height="auto" controls>
					<source src={media.videoLink} type={getFileMimeTypeFromString(media.videoLink)} />
					Your browser does not support the video tag.
				</video>
			</Modal>

			{/* Main input row */}
			<form
				ref={containerRef}
				onSubmit={handleSubmit}
				className="flex items-end gap-2 rounded border border-solid border-grey-5 bg-white px-2 py-1"
			>
				<div className="flex shrink-0 items-center gap-1">
					<Tooltip title="Cancel">
						<Button shape="circle" icon={<DeleteOutlined />} onClick={onCancel}></Button>
					</Tooltip>
					<>
						<ChatUploader
							name="file"
							multiple={true}
							showUploadList={false}
							beforeUpload={imageUploadValidation}
							onChange={media.handlePhotoUploadChange}
							id={workOrderId}
							accept={imageMimeTypes}
							fileList={media.photoFileList}
						>
							<Tooltip title="Upload photo">
								<Button shape="circle" icon={<CameraOutlined />}></Button>
							</Tooltip>
						</ChatUploader>
						<ChatUploader
							name="file"
							showUploadList={false}
							beforeUpload={beforeVideoUpload}
							onChange={media.handleVideoUploadChange}
							accept={videoMimeTypes.join(',')}
							id={workOrderId}
							fileList={media.videoFileList}
						>
							<Tooltip title="Upload video">
								<Button shape="circle" icon={<VideoCameraAddOutlined />}></Button>
							</Tooltip>
						</ChatUploader>
					</>
				</div>

				{/* Text input */}
				<div className="flex-1">
					<MentionsInput
						placeholder="Type a reply..."
						forceSuggestionsAboveCursor
						suggestionsPortalHost={document.body}
						value={noteText}
						onChange={(e, newValue) => setNoteText(newValue)}
						className="row-chat-input"
						allowSpaceInQuery={true}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								stopDefaultEvents(e);
								handleSubmit(e);
							}
							if (e.key === 'Escape' && onCancel) {
								stopDefaultEvents(e);
								onCancel();
							}
						}}
					>
						<Mention
							trigger="@"
							data={onSearch}
							className="bg-[#d2d9de]"
							renderSuggestion={(user, search, highlightedDisplay, index, focused) => {
								const isInactive = compareStrings(user.status, 'inactive');
								return (
									<div
										className={
											'flex flex-row items-center p-2 px-4 ' +
											(isInactive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')
										}
										onClick={(e) => {
											if (isInactive) {
												e.preventDefault();
												e.stopPropagation();
											}
										}}
									>
										<UserName
											contact={user}
											onClick={() => {}}
											status={user.status === 'not_available' ? undefined : user.status}
										/>
									</div>
								);
							}}
						/>
					</MentionsInput>
				</div>

				<div className="flex shrink-0 items-center">
					{media.fileUploading ? (
						<Tooltip
							title={
								'Uploading ' +
								media.uploadingFileCount +
								(media.uploadingFileCount === 1 ? ' file' : ' files')
							}
						>
							<Progress type="circle" percent={media.uploadProgress} width={30} />
						</Tooltip>
					) : hasText ? (
						<Tooltip title="Send">
							<Button
								shape="circle"
								className="border-none"
								onClick={handleSubmit}
								icon={<SendOutlined />}
							/>
						</Tooltip>
					) : (
						<AudioRecorder
							popoverPlacement="topLeft"
							sendLabel="Send"
							modalTitle="Send Audionote"
							containerType="popover"
							onSend={handleSendAudio}
						>
							<Tooltip title="Record voice note">
								<Button
									shape="circle"
									className="border-none"
									icon={<AudioOutlined className="text-character-secondary" />}
								/>
							</Tooltip>
						</AudioRecorder>
					)}
				</div>
			</form>
		</div>
	);
};

export default RowChatInput;
